import { AgGridAngular } from '@ag-grid-community/angular';
import { ColDef } from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AgGridService,
  HeaderRendererComponent,
  ResponsiveService,
} from '@frk/eds-components';
import { FundNameCellRendererComponent } from '@products/fund-listing/ag-grid-helpers/cell-renderers/fund-name-cell-renderer/fund-name-cell-renderer.component';
import { RatingsCellRendererComponent } from '@products/fund-listing/ag-grid-helpers/cell-renderers/ratings-cell-renderer/ratings-cell-renderer.component';
import { AsyncContentManagerService } from '@services';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { TranslateService } from '@shared/translate/translate.service';
import { FundShareClassId } from '@types';
import { Logger } from '@utils/logger';
import { Observable, Subject } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';
import { FundNameGroupHeaderComponent } from './fund-name-group-header/fund-name-group-header.component';
import {
  GridData,
  MorningstarTableService,
} from './services/morningstar-table.service';

const logger = Logger.getLogger('MorningstarTableComponent');

interface MorningstarDto {
  title: string;
  funds: string;
}

@Component({
  selector: 'ft-morningstar-table',
  templateUrl: './morningstar-table.component.html',
  styleUrls: ['./morningstar-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MorningstarTableComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  private shareClasses: FundShareClassId[];
  private unsubscribe$: Subject<void> = new Subject<void>();
  public isHandheld$: Observable<boolean>;
  public defaultColDef: ColDef;
  public grids$: Observable<GridData[]>;
  public frameworkComponents: object = {
    fundNameCellRendererComponent: FundNameCellRendererComponent,
    ratingsCellRenderComponent: RatingsCellRendererComponent,
    fundNameGroupHeaderComponent: FundNameGroupHeaderComponent,
    headerRendererComponent: HeaderRendererComponent,
  };
  public titleTooltip: string;

  @ViewChild('ratingsGrid', { static: false, read: AgGridAngular })
  ratingsGrid: AgGridAngular;
  @ViewChild('ratingsGrid', { static: false, read: ElementRef })
  ratingsEl: ElementRef;

  constructor(
    private cdr: ChangeDetectorRef,
    private ratingService: MorningstarTableService,
    private responsiveService: ResponsiveService,
    private translateService: TranslateService,
    private acm: AsyncContentManagerService,
    private agGridService: AgGridService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.isHandheld$ = this.responsiveService.isHandheld$();

    const morningstarDto: MorningstarDto = this.document?.getData<MorningstarDto>();
    logger.debug('morningstarDto', morningstarDto);
    this.parseConfig(morningstarDto);

    this.titleTooltip = this.translateService.tooltip(
      'products.morningstar_rating-title'
    );

    this.defaultColDef = this.ratingService.getGridDefaultColDef();
    logger.debug('defaultColDef', this.defaultColDef);

    logger.debug(
      'shareClasses to display',
      this.shareClasses,
      morningstarDto?.funds
    );
    if (this.shareClasses) {
      this.grids$ = this.ratingService.getGridData$(this.shareClasses);

      // let AsyncContentmanager know when component has rendered
      // this waits for grid data to emit, adds a 10ms delay, then reports component is ready
      this.grids$
        .pipe(takeUntil(this.unsubscribe$), delay(10))
        .subscribe((): void => {
          this.acm.update(this.component?.getId(), true);
        });
    }

    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    this.agGridService.removeGrid(this.ratingsGrid);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * prevent overlapping of columns
   */
  public onGridReady() {
    this.agGridService.registerGrid(this.ratingsGrid, this.ratingsEl);
    this.ratingsGrid.api.resetRowHeights();
  }

  public onColumnResized() {
    this.ratingsGrid.api.resetRowHeights();
  }

  /**
   * Change detection on sorting
   */
  public onSortChanged = () => this.cdr.detectChanges();

  private parseConfig(morningstarDto: MorningstarDto): void {
    // fund data comes as a string e.g.
    // "fundList_csv: 90719 4909\r\nfundShareClassList_csv: 90614-AA1 4909-BC"
    // TODO: this is quick and dirty regex parsing. Data format could be improved in future
    this.shareClasses = morningstarDto?.funds.match(
      /[A-Za-z\d]+-[A-Za-z\d]+/g
    ) as FundShareClassId[];
  }
}
